import { ArrowRight, ArrowDown, Sparkles, Zap, Globe, Shield, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ScrollReveal, fadeInLeft, fadeInRight, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";

export function SolutionsHeroNew() {
  return (
    <section className="relative min-h-screen flex flex-col pt-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-subtle" />
      
      {/* Ambient orbs */}
      <motion.div
        className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full pointer-events-none ambient-orb"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)',
          filter: 'blur(80px)'
        }}
      />
      <motion.div
        className="absolute bottom-1/3 left-[10%] w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--accent) / 0.08) 0%, transparent 70%)',
          filter: 'blur(60px)'
        }}
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex-1 flex flex-col">
        {/* Top Row - Large Typography */}
        <div className="pt-16 md:pt-24">
          <div className="flex items-start gap-8">
            <ScrollReveal variants={fadeInLeft}>
              <motion.div 
                className="inline-flex items-center gap-2 badge-primary mb-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Enterprise Solutions
              </motion.div>
              <h1 className="text-5xl md:text-6xl lg:text-8xl font-extrabold leading-[0.85] tracking-tight mb-6">
                <span className="text-foreground block">Powering the</span>
                <span className="text-gradient block py-2">Electric Future</span>
              </h1>
            </ScrollReveal>
          </div>
        </div>

        {/* Middle Content - Asymmetric */}
        <div className="flex-1 flex items-center py-12">
          <div className="grid lg:grid-cols-12 gap-8 w-full">
            {/* Left Side - Decorative line */}
            <motion.div
              className="lg:col-span-5 hidden lg:flex items-end pb-12"
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <div className="w-full section-divider" />
            </motion.div>

            {/* Right Side - Description & CTA */}
            <ScrollReveal variants={fadeInRight} className="lg:col-span-7 flex flex-col justify-center">
              <p className="text-lg md:text-xl text-muted-foreground max-w-lg mb-10 leading-relaxed font-light">
                From fleet electrification to grid services, we deliver infrastructure solutions that transform how
                businesses and cities power mobility.
              </p>

              <motion.div
                className="flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <a href="https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/" target="_blank" rel="noopener noreferrer">
                    <Button variant="hero" size="lg" className="group text-base px-8 py-6 shadow-lg shadow-primary/20">
                      Start Your Project
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </a>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50 transition-all text-base px-8 py-6"
                  >
                    View Case Studies
                  </Button>
                </motion.div>
              </motion.div>
            </ScrollReveal>
          </div>
        </div>

        {/* Bottom Stats Bar - Enhanced */}
        <motion.div
          className="border-t border-border/20 py-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-6" staggerDelay={0.1}>
            {[
              { number: "20K+", label: "Charging Ports", icon: Zap },
              { number: "99.9%", label: "Network Uptime", icon: Activity },
              { number: "150+", label: "Enterprise Clients", icon: Globe },
              { number: "24/7", label: "Global Support", icon: Shield },
            ].map((stat, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  className="group glass-card p-4 text-center"
                  whileHover={{ scale: 1.03, borderColor: "hsl(var(--primary) / 0.3)" }}
                >
                  <div className="text-3xl md:text-4xl font-bold text-primary tracking-tight group-hover:text-accent transition-colors">
                    {stat.number}
                  </div>
                  <div className="text-sm text-white/60 font-medium">{stat.label}</div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="flex justify-center pb-8"
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <a
            href="#solutions-grid"
            className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors group"
          >
            <span className="text-xs uppercase tracking-widest font-medium">Explore Solutions</span>
            <div className="w-8 h-8 rounded-full border border-border/50 flex items-center justify-center group-hover:border-primary/50 transition-colors">
              <ArrowDown className="h-4 w-4" />
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
}